import React, { useState, createContext } from 'react';
import { API } from 'aws-amplify';
import { useAuth } from './Auth';
import { DBMDataInterface } from '../models/DBMDataInterface';
import { useApiError } from './ApiErrorProvider';
import { error } from '../constants/bindings';
import { dates as monthsShort } from '../constants/dates';

const dbmDefault = {
  dbmlist: []
} as DBMDataInterface;

const DBMContext = createContext({
  loading: true,
  getDBMYearlyReports: (search) => {},
  getDBMMonthlyReports: (search) => {},
  dbmMonthlyData: dbmDefault as DBMDataInterface,
  dbmYearlyData: dbmDefault as DBMDataInterface
});

export const DBMProvider = (props) => {
  const { user } = useAuth();
  const [dbmMonthlyData, setDBMMonthlyData] =
    useState<DBMDataInterface>(dbmDefault);
  const [dbmYearlyData, setDBMYearlyData] =
    useState<DBMDataInterface>(dbmDefault);
  const [loading, setLoading] = useState(true);
  const { setErrorCode } = useApiError();

  const getDBMMonthlyReports = async (search) => {
    if (!user || !search || !search.selectedDate) {
      return;
    }
    setLoading(true);
    const month =
      monthsShort.findIndex(
        (month) => month === search.selectedDate.split(' ')[0]
      ) + 1;
    const year = Number(search.selectedDate.split(' ')[1]);
    const requestObject = {
      body: {
        year,
        month,
        baseAirport: search.selectedBase,
        position: search.selectedPosition,
        equipment: search.selectedEquipment
      }
    };
    if (search.selectedBase?.toLowerCase() === 'all') {
      delete requestObject.body.baseAirport;
    }
    if (search.selectedPosition?.toLowerCase() === 'all') {
      delete requestObject.body.position;
    }
    if (search.selectedEquipment?.toLowerCase() === 'all') {
      delete requestObject.body.equipment;
    }
    try {
      const monthlyReports = await API.post(
        'crewlife',
        '/dbm/monthly',
        requestObject
      );
      if (!monthlyReports) {
        setDBMMonthlyData(dbmDefault);
        return;
      }
      const monthlyReportsWithId = monthlyReports.dbmlist.map((d, i) => ({
        ...d,
        id: i
      }));
      setDBMMonthlyData({ ...monthlyReports, dbmlist: monthlyReportsWithId });
      return monthlyReportsWithId;
    } catch (e: any) {
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
    } finally {
      setLoading(false);
    }
  };

  const getDBMYearlyReports = async (search) => {
    if (!user || !search) {
      return;
    }
    setLoading(true);
    const yearRange = search.selectedDate.split(' - ');
    const fromyear = parseInt(yearRange[0].split(' ')[1]);
    const toyear = parseInt(yearRange[1].split(' ')[1]);
    const requestObject = {
      body: {
        fromyear,
        toyear,
        baseAirport: search.selectedBase,
        position: search.selectedPosition,
        equipment: search.selectedEquipment
      }
    };
    if (search.selectedBase?.toLowerCase() === 'all') {
      delete requestObject.body.baseAirport;
    }
    if (search.selectedPosition?.toLowerCase() === 'all') {
      delete requestObject.body.position;
    }
    if (search.selectedEquipment?.toLowerCase() === 'all') {
      delete requestObject.body.equipment;
    }

    try {
      const yearlyReports = await API.post(
        'crewlife',
        '/dbm/yearly',
        requestObject
      );
      // we must set ids for the Xgrid table
      const yearlyReportsWithId = yearlyReports.dbmlist.map((d, i) => ({
        ...d,
        id: i,
        avg: d.avg ? Math.round(d.avg * 100) / 100 : 0
      }));
      if (!yearlyReportsWithId) {
        setDBMYearlyData(dbmDefault);
        return;
      }
      setDBMYearlyData({ ...yearlyReports, dbmlist: yearlyReportsWithId });
      setLoading(false);
      return yearlyReports;
    } catch (e: any) {
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DBMContext.Provider
      value={{
        loading,
        getDBMYearlyReports,
        getDBMMonthlyReports,
        dbmMonthlyData,
        dbmYearlyData
      }}
    >
      {props.children}
    </DBMContext.Provider>
  );
};

export const useDBM = () => {
  const context = React.useContext(DBMContext);
  return context;
};
